import * as React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { animate } from 'motion';
import { FacetProps } from '../state/Context';
import { useSearch } from '../hooks';

export interface IFacetsCategory {
  name: string;
  label: string;
  tags: FacetProps[];
  expanded: boolean;
}

const FacetsCategory: React.FC<IFacetsCategory> = ({
  name,
  label,
  tags,
  expanded = false
}) => {
  const {
    state: { selectedFacets },
    addFacet,
    removeFacet
  } = useSearch();
  const [isExpanded, setIsExpanded] = React.useState(expanded);
  const uuid = React.useMemo(() => uuidv4(), []);
  const panelEl = React.useRef<HTMLDivElement>(null);

  const togglePanel = React.useCallback(
    async (el: HTMLDivElement, inner: HTMLUListElement, expand: boolean) => {
      if (expand) {
        inner.style.display = 'block';

        await animate(
          el,
          {
            height: `${inner.clientHeight}px`
          },
          {
            duration: 0.3
          }
        ).finished;

        el.style.height = 'auto';
      } else {
        el.style.height = `${inner.clientHeight}px`;
        await animate(
          el,
          {
            height: 0
          },
          {
            duration: 0.3
          }
        ).finished;
        inner.style.display = 'none';
      }
    },
    []
  );

  React.useEffect(() => {
    const listEl = panelEl.current?.querySelector('ul');
    if (panelEl.current && listEl) {
      togglePanel(panelEl.current, listEl, isExpanded);
    }
  }, [isExpanded]);

  const handleCheckboxChange = React.useCallback(
    (id: string) => {
      if (!isSelected(id)) {
        addFacet(name, id);
      } else {
        removeFacet(name, id);
      }
    },
    [selectedFacets]
  );

  const isSelected = React.useCallback(
    (id: string) => {
      return !!selectedFacets[name] && !!selectedFacets[name].includes(id);
    },
    [selectedFacets]
  );

  if (!tags.filter(tag => !!tag.count).length) {
    return null;
  }

  return (
    <div className="search__facets-category">
      <button
        type="button"
        className="search__facets-category__trigger"
        id={`trigger-${uuid}`}
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded(!isExpanded)}
        aria-controls={`panel-${uuid}`}
      >
        <div>
          {label}{' '}
          {!!selectedFacets[name]?.length && (
            <span>({selectedFacets[name].length})</span>
          )}
        </div>
        <svg role="presentation">
          <use xlinkHref="#chevron-down" />
        </svg>
      </button>

      <div
        ref={panelEl}
        role="region"
        className="search__facets-category__panel"
        id={`panel-${uuid}`}
        aria-labelledby={`trigger-${uuid}`}
      >
        <ul>
          {tags
            .filter(tag => !!tag.count)
            .map(tag => (
              <li key={tag.id}>
                <label className="search__facets-category__item">
                  <div>
                    <input
                      type="checkbox"
                      checked={isSelected(tag.id)}
                      onChange={() => handleCheckboxChange(tag.id)}
                    />
                    {tag.name}
                  </div>
                  <span>{tag.count}</span>
                </label>
              </li>
            ))}
        </ul>
      </div>
    </div>
  );
};

export default FacetsCategory;
